import { z } from 'zod';
import { FieldTypeEnum } from './custom-field.types';
import { ModuleTypeEnum, isValidModuleType } from './module-type';

// Re-export for convenience
export { ModuleTypeEnum, isValidModuleType };

/**
 * Schema for module reference (used in polymorphic relationships)
 */
export const ModuleReferenceSchema = z.object({
  moduleType: z.nativeEnum(ModuleTypeEnum),
  moduleId: z.string().uuid(),
});

export type ModuleReference = z.infer<typeof ModuleReferenceSchema>;

/**
 * Schema for standard module column definition
 */
export const ModuleColumnDefinitionSchema = z.object({
  id: z.string(), // Unique identifier, usually matching DB field
  name: z.string(), // Technical name
  label: z.string(), // Display label
  description: z.string().optional(), // Optional description
  isVisible: z.boolean().default(true), // Default visibility
  isRequired: z.boolean().default(false), // Whether field is required
  isSystem: z.boolean().default(false), // System fields can't be disabled
  isFixed: z.boolean().optional().default(false), // Fixed columns cannot be moved (optional with default)
  isReadOnly: z.boolean().optional(), // Read-only fields cannot be edited by users (defaults to false)
  order: z.number().int().default(0), // Display order
  fieldType: z.enum(Object.values(FieldTypeEnum) as [string, ...string[]]), // Use shared field type enum
  width: z.string().optional(), // Width of the column (CSS value)
  minWidth: z.string().optional(), // Minimum width of the column (CSS value)
  icon: z.string().optional(), // Optional: The name of the lucide-react icon to display
  // Client-side sort: push null/empty values to the end in BOTH directions
  // instead of letting them coerce to 0 (the TanStack default). Use for signed
  // numeric columns where "no value" must not sort between negatives and
  // positives (e.g. the T7 derived-vs-rep gap).
  sortNullsLast: z.boolean().optional(),
  options: z.array(z.object({
    value: z.string(),
    label: z.string(),
    color: z.string().optional(),
  })).optional(), // Options for dropdown/select fields
});

export type ModuleColumnDefinition = z.infer<typeof ModuleColumnDefinitionSchema>;

/**
 * Schema for user's column visibility preferences
 */
export const ColumnVisibilityPreferenceSchema = z.object({
  moduleType: z.nativeEnum(ModuleTypeEnum),
  organizationId: z.string().uuid(),
  userId: z.string().uuid().optional(), // If null, applies org-wide
  columnPreferences: z.record(z.string(), z.boolean()), // Map of column IDs to visibility
  columnOrders: z.record(z.string(), z.number()).optional(), // Map of column IDs to order
});

export type ColumnVisibilityPreference = z.infer<typeof ColumnVisibilityPreferenceSchema>;

/**
 * Helper function to create a module reference
 */
export function createModuleReference(moduleType: ModuleTypeEnum, moduleId: string): ModuleReference {
  return {
    moduleType,
    moduleId,
  };
}

/**
 * Helper function to get the appropriate module type for a given entity
 */
export function getModuleTypeForEntity(entityType: string): ModuleTypeEnum {
  const typeMap: Record<string, ModuleTypeEnum> = {
    'project': ModuleTypeEnum.PROJECT,
    'task': ModuleTypeEnum.TASK,
    'ticket': ModuleTypeEnum.TICKET,
    'deal': ModuleTypeEnum.DEAL,
    'lead': ModuleTypeEnum.LEAD,
    'event': ModuleTypeEnum.EVENT,
    'client': ModuleTypeEnum.ENTITY,
    'contract': ModuleTypeEnum.CONTRACT,
    'invoice': ModuleTypeEnum.INVOICE,
    'expense': ModuleTypeEnum.EXPENSE,
    'thread': ModuleTypeEnum.THREAD,
    'timesheet': ModuleTypeEnum.TIME_ENTRY,
    'time_entry': ModuleTypeEnum.TIME_ENTRY,
    'team': ModuleTypeEnum.TEAM,
    'inventory': ModuleTypeEnum.INVENTORY,
    'document': ModuleTypeEnum.DOCUMENT,
    'organization': ModuleTypeEnum.ORGANIZATION,
    'workflow': ModuleTypeEnum.WORKFLOW
  };

  return typeMap[entityType.toLowerCase()] || ModuleTypeEnum.PROJECT;
}

/**
 * Helper function to convert ModuleTypeEnum to Prisma ModuleType
 * This is useful when passing module types to Prisma queries
 */
export function toPrismaModuleType(moduleType: ModuleTypeEnum | null | undefined): any {
  if (!moduleType) return null;
  // Since the enum values are the same in both types,
  // we can just pass it through, but cast it to 'any'
  // to avoid TypeScript trying to enforce the Prisma type
  return moduleType as any;
} 
